/**
 * HandFullModal.tsx — WEB.4.R2.CF
 *
 * Shown when a card is drawn but the player's hand is already full (5/5).
 * Lets the player either replace one of the 5 existing cards or discard the
 * newly drawn card.
 *
 * Case B (paid card after Buy): buyCost already debited before this modal opens.
 * Case C/D (free card): no cost. Discarding here is free.
 * Case Auction (R20-GE-10): winner's hand full — swap or forfeit (no debit on forfeit).
 *
 * Confirmation prompts on all actions (irreversible).
 *
 * VISIBLE WHEN: handFullPendingCard !== null OR pendingAuctionHandFull !== null
 * TEST PATH: fill hand to 5 → interact with newsgirl/bureaucrat → HandFullModal appears.
 *            OR: fill hand to 5 → win policy auction → HandFullModal appears for winner.
 */

import type React from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { getCardById, useGameStore } from "../../store/game-store.js";
import type { SeatIndex } from "../../store/game-store.js";
import styles from "./HandFullModal.module.css";

export function HandFullModal(): React.ReactElement | null {
  const { t } = useI18n();
  const handFullPendingCard = useGameStore((s) => s.handFullPendingCard);
  // R20-GE-10: auction winner hand-full alternate trigger.
  const pendingAuctionHandFull = useGameStore((s) => s.pendingAuctionHandFull);
  // R11.2 invariant: never render over a moving board.
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const hands = useGameStore((s) => s.hands);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const resolveHandFull = useGameStore((s) => s.resolveHandFull);
  const commitAuctionHandFull = useGameStore((s) => s.commitAuctionHandFull);

  if (boardAnimationActive) return null;
  // dismissHandFull intentionally NOT used here — HIGH 2 fix removed the backdrop-click
  // path that called it. dismissHandFull is still available for programmatic callers.

  // ── Auction hand-full path (R20-GE-10) ───────────────────────────────────────
  if (pendingAuctionHandFull !== null) {
    const { winnerSeat, wonCard, bidAmount } = pendingAuctionHandFull;
    const auctionHand = hands[winnerSeat] ?? [];
    const wonDef = getCardById(wonCard.id);
    const wonName = wonDef?.cardName ?? wonCard.id;

    function handleAuctionReplace(idx: number): void {
      const existingDef = getCardById(auctionHand[idx]?.id ?? "");
      const existingName = existingDef?.cardName ?? auctionHand[idx]?.id ?? `Card ${idx + 1}`;
      const ok = window.confirm(
        `Replace ${existingName} with ${wonName}? (Bid of ${bidAmount}T will be charged.)`,
      );
      if (ok) commitAuctionHandFull(idx);
    }

    function handleAuctionForfeit(): void {
      const ok = window.confirm(`Forfeit ${wonName}? Your hand stays as-is — no tokens charged.`);
      if (ok) commitAuctionHandFull("forfeit");
    }

    return (
      <div className={styles.overlay} data-testid="hand-full-modal-overlay">
        <div className={styles.panel} data-testid="hand-full-modal">
          <div className={styles.title} data-testid="hand-full-title">
            ✋ HAND FULL (5/5) — AUCTION WIN
          </div>
          <div className={styles.subtitle}>
            Won: <span className={styles.pendingName}>{wonName}</span> (bid: {bidAmount}T)
          </div>
          <div className={styles.instruction}>{t("handFull.prompt")}</div>

          <div className={styles.slotList} data-testid="hand-full-slot-list">
            {auctionHand.map((card, idx) => {
              const def = getCardById(card.id);
              const name = def?.cardName ?? card.id;
              const typeClass =
                def?.cardType === "Policy"
                  ? styles.slotPolicy
                  : def?.cardType === "Mitigate"
                    ? styles.slotMitigate
                    : styles.slotEvent;
              return (
                <button
                  key={card.instanceId}
                  type="button"
                  className={[styles.slotBtn, typeClass].join(" ")}
                  onClick={() => handleAuctionReplace(idx)}
                  data-testid={`hand-full-replace-${idx}`}
                >
                  <span className={styles.slotLabel}>{t("handFull.replace")}</span>
                  <span className={styles.slotName}>{name}</span>
                  {def?.cardType !== undefined && (
                    <span className={styles.slotType}>{def.cardType}</span>
                  )}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className={styles.discardBtn}
            onClick={handleAuctionForfeit}
            data-testid="hand-full-discard-new"
          >
            Forfeit ({wonName}) — no charge
          </button>
        </div>
      </div>
    );
  }

  // ── Normal hand-full path ─────────────────────────────────────────────────────
  if (handFullPendingCard === null) return null;

  const seatIdx = currentSeat as SeatIndex;
  const hand = hands[seatIdx] ?? [];
  const pendingDef = getCardById(handFullPendingCard.id);
  const pendingName = pendingDef?.cardName ?? handFullPendingCard.id;

  function handleReplace(idx: number): void {
    const existingDef = getCardById(hand[idx]?.id ?? "");
    const existingName = existingDef?.cardName ?? hand[idx]?.id ?? `Card ${idx + 1}`;
    const ok = window.confirm(`Replace ${existingName} with ${pendingName}?`);
    if (ok) resolveHandFull(idx);
  }

  function handleDiscard(): void {
    const ok = window.confirm(`Discard ${pendingName}? You won't get it back.`);
    if (ok) resolveHandFull("discard");
  }

  return (
    // HIGH 2 fix (Codex R2): backdrop click removed. HandFullModal is a forced
    // decision — player MUST click one of the 6 buttons (5 Replace-X or 1 Discard-new).
    // Backdrop click previously called dismissHandFull() with no confirmation,
    // silently dropping the card (including post-buy cards where cost was already debited).
    <div className={styles.overlay} data-testid="hand-full-modal-overlay">
      <div className={styles.panel} data-testid="hand-full-modal">
        <div className={styles.title} data-testid="hand-full-title">
          ✋ HAND FULL (5/5)
        </div>
        <div className={styles.subtitle}>
          Drew: <span className={styles.pendingName}>{pendingName}</span>
        </div>
        <div className={styles.instruction}>{t("handFull.prompt")}</div>

        <div className={styles.slotList} data-testid="hand-full-slot-list">
          {hand.map((card, idx) => {
            const def = getCardById(card.id);
            const name = def?.cardName ?? card.id;
            const typeClass =
              def?.cardType === "Policy"
                ? styles.slotPolicy
                : def?.cardType === "Mitigate"
                  ? styles.slotMitigate
                  : styles.slotEvent;
            return (
              <button
                key={card.instanceId}
                type="button"
                className={[styles.slotBtn, typeClass].join(" ")}
                onClick={() => handleReplace(idx)}
                data-testid={`hand-full-replace-${idx}`}
              >
                <span className={styles.slotLabel}>{t("handFull.replace")}</span>
                <span className={styles.slotName}>{name}</span>
                {def?.cardType !== undefined && (
                  <span className={styles.slotType}>{def.cardType}</span>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className={styles.discardBtn}
          onClick={handleDiscard}
          data-testid="hand-full-discard-new"
        >
          Discard new card ({pendingName})
        </button>
      </div>
    </div>
  );
}
